import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Title,
  Text,
  Button,
  Group,
  Stack,
  Card,
  Badge,
  TextInput,
  Textarea,
  Select,
  Autocomplete,
  Modal,
  Checkbox,
  ActionIcon,
  Tooltip,
  Alert,
  Divider,
  Code,
  Collapse,
  Box,
  Menu
} from '@mantine/core';
import {
  IconArrowLeft,
  IconAlertCircle,
  IconAlertTriangle,
  IconPlus,
  IconTrash,
  IconCode,
  IconCheck,
  IconDownload,
  IconPlayerPlayFilled,
  IconNotes,
  IconChevronDown,
  IconCircleCheckFilled
} from '@tabler/icons-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import YAML from 'yaml';
import PlanStepCard, { makeBlankStep } from './PlanStepCard';
import PreconditionsBadge, { hasPreconditions } from './PreconditionsBadge';
import { reorderForPreconditionGrouping } from '../utils/batchRunner';
import { isSitBackendConfigured } from '../api_scripts/sitApiClient';
import { savePlansBatch, listFolders } from '../api_scripts/plansClient';
import { createBatch } from '../api_scripts/batchesClient';
import { useDids } from '../hooks/useDids';
import { useAuth } from '../../../shared/contexts/AuthContext';

const SUBFOLDER_PATTERN = /^[A-Za-z0-9][A-Za-z0-9 _-]{0,79}$/;

const CCAAS_OPTIONS = [
  { value: 'wxcc', label: 'WebEx Contact Center' },
  { value: 'aws-connect', label: 'AWS Connect' },
  { value: 'five9', label: 'Five9' },
  { value: 'genesys', label: 'Genesys' }
];

const STEP_TYPE_LABELS = {
  expectPrompt: 'Expect prompt',
  pressDtmf: 'Press digits',
  saySpeech: 'Speak',
  expectTransfer: 'Expect transfer',
  hangup: 'End call'
};

function makeStepId(plan, type) {
  const prefix = type.replace(/([A-Z])/g, '-$1').toLowerCase();
  const existing = new Set((plan.steps || []).map((s) => s.id));
  for (let i = 1; i < 1000; i++) {
    const candidate = `${prefix}-${i}`;
    if (!existing.has(candidate)) return candidate;
  }
  return `${prefix}-${Date.now()}`;
}

function shallowClonePlan(plan) {
  return {
    ...plan,
    metadata: { ...(plan.metadata || {}) },
    target: { ...(plan.target || {}) },
    execution: plan.execution ? { ...plan.execution } : undefined,
    steps: (plan.steps || []).map((s) => ({ ...s }))
  };
}

function VerifyPlansScreen({ onBack }) {
  const location = useLocation();
  const navigate = useNavigate();
  const backendConfigured = isSitBackendConfigured();
  const { dids, loading: didsLoading } = useDids();
  // AuthContext exposes the evaluated `user` object (not getUserInfo) on
  // the context value — keep this in sync if AuthContext is ever
  // restructured. user.email mirrors the JWT email the backend reads as
  // createdBy.
  const { user } = useAuth();
  const myEmail = (user?.email || '').toLowerCase();

  // Plans arrive via location.state from the importers. If the user lands
  // here directly (e.g. on refresh), bail back to the landing page.
  const [plans, setPlans] = useState(() => {
    const incoming = location.state?.plans;
    if (!Array.isArray(incoming)) return null;
    return incoming.map(shallowClonePlan);
  });
  const sourceLabel = location.state?.source || 'import';
  const [approved, setApproved] = useState(() => new Set());
  const [yamlOpen, setYamlOpen] = useState(() => new Set());
  const [bannerError, setBannerError] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  // Folder picker state — opens before savePlansBatch fires.
  const [folderPickerOpen, setFolderPickerOpen] = useState(false);
  const [myFolders, setMyFolders] = useState([]);

  useEffect(() => {
    if (plans === null) navigate('/');
  }, [plans, navigate]);

  const updateMetadata = (idx, patch) => {
    setPlans((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], metadata: { ...next[idx].metadata, ...patch } };
      return next;
    });
  };
  const updateTarget = (idx, patch) => {
    setPlans((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], target: { ...next[idx].target, ...patch } };
      return next;
    });
  };
  const updateSteps = (idx, updater) => {
    setPlans((prev) => {
      const next = [...prev];
      const steps = updater(next[idx].steps || []);
      next[idx] = { ...next[idx], steps };
      return next;
    });
  };

  const toggleApproved = (idx) => {
    setApproved((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const toggleYaml = (idx) => {
    setYamlOpen((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const discardPlan = (idx) => {
    setPlans((prev) => prev.filter((_, i) => i !== idx));
    setApproved((prev) => {
      const next = new Set();
      prev.forEach((v) => {
        if (v < idx) next.add(v);
        else if (v > idx) next.add(v - 1);
      });
      return next;
    });
  };

  const approvedPlans = useMemo(
    () => (plans || []).filter((_, i) => approved.has(i)),
    [plans, approved]
  );

  const allApproved = plans && plans.length > 0 && approved.size === plans.length;
  const setAllApproved = (on) => {
    if (!plans) return;
    setApproved(on ? new Set(plans.map((_, i) => i)) : new Set());
  };

  const handleDownloadApproved = () => {
    if (approvedPlans.length === 0) return;
    approvedPlans.forEach((plan) => {
      const yaml = YAML.stringify(plan);
      const blob = new Blob([yaml], { type: 'text/yaml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${plan.metadata?.id || 'plan'}.yaml`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    });
  };

  const handleSendApprovedToRun = async () => {
    if (approvedPlans.length === 0) return;
    // No precondition prompt here — both paths defer to the runtime gate:
    // - Single-plan flow hands off to RunTrigger, which pauses immediately
    //   before dialing if the plan has preconditions.
    // - Multi-plan flow creates a backend batch row; the runner pauses the
    //   loop before each plan whose preconditions differ from the previous.
    setSaving(true);
    setBannerError(null);
    setSaveStatus(null);
    try {
      // Single-plan flow stays in the RunTrigger editor (lets the user tweak
      // destination, see the YAML, etc.).
      if (approvedPlans.length === 1) {
        navigate('/run', { state: { prefillYaml: YAML.stringify(approvedPlans[0]) } });
        return;
      }
      // Reorder so plans sharing IVR setup run back-to-back — minimizes the
      // engineer's flag-flipping ceremony.
      const queue = reorderForPreconditionGrouping(approvedPlans);
      // When any plan needs manual IVR setup, route through RunTrigger's
      // browser-driven batch runner — the runtime gate (pause / resume /
      // skip / cancel) needs an active SPA session.
      //
      // We create the batch row FIRST (mode='inbrowser') so the Batches tab
      // shows the batch as soon as it starts dialing, then thread the
      // batchId through to RunTrigger. runBatchSequentially POSTs progress
      // updates as each plan finalizes so the Batches tab reflects in-browser
      // batches the same way it shows server-driven ones.
      if (queue.some(hasPreconditions)) {
        const inBrowserBatch = await createBatch({
          plans: queue,
          overrides: {
            ...(queue[0]?.target?.destination ? { destination: queue[0].target.destination } : {}),
            ...(queue[0]?.target?.fromDid ? { fromDid: queue[0].target.fromDid } : {})
          },
          label: `${queue.length} plans — ${sourceLabel}`,
          mode: 'inbrowser'
        });
        const [first, ...rest] = queue;
        navigate('/run', {
          state: {
            prefillYaml: YAML.stringify(first),
            batchPlans: rest,
            inBrowserBatchId: inBrowserBatch.batchId
          }
        });
        return;
      }
      const firstTarget = queue[0]?.target || {};
      const batch = await createBatch({
        plans: queue,
        overrides: {
          ...(firstTarget.destination ? { destination: firstTarget.destination } : {}),
          ...(firstTarget.fromDid ? { fromDid: firstTarget.fromDid } : {})
        },
        label: `${queue.length} plans — ${sourceLabel}`
      });
      navigate(`/history/batches/${batch.batchId}`);
    } catch (err) {
      setBannerError(
        `Failed to create batch: ${err?.response?.data?.message || err.message}`
      );
    } finally {
      setSaving(false);
    }
  };

  const openFolderPicker = async () => {
    if (approvedPlans.length === 0) return;
    setBannerError(null);
    setSaveStatus(null);
    // Best-effort prefetch — the picker still works if the network call
    // fails (autocomplete falls back to no suggestions).
    try {
      const tree = await listFolders();
      const mine = tree.find((entry) => entry.engineer === myEmail);
      // "Uncategorized" is the virtual bucket the API returns for legacy
      // rows that pre-date this feature — surfacing it in the autocomplete
      // would let the engineer save NEW plans under that name, which the
      // backend also rejects. Filter it out client-side too so the UX
      // doesn't suggest something it'll reject on submit.
      setMyFolders((mine?.subfolders?.map((s) => s.name) || []).filter((n) => n !== 'Uncategorized'));
    } catch {
      setMyFolders([]);
    }
    setFolderPickerOpen(true);
  };

  const handleSaveToLibrary = async (subfolder) => {
    if (approvedPlans.length === 0) return;
    setFolderPickerOpen(false);
    setSaving(true);
    setBannerError(null);
    setSaveStatus(null);
    try {
      const { saved, errors } = await savePlansBatch(approvedPlans, subfolder);
      setSaveStatus({ saved: saved.length, errors, subfolder });
      if (errors.length === 0) {
        setBannerError(null);
      }
    } catch (err) {
      setBannerError(`Save to Library failed: ${err?.response?.data?.message || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (!plans) return null;
  if (plans.length === 0) {
    return (
      <Container size="lg" py="xl">
        <Group mb="md">
          <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={onBack}>
            Back
          </Button>
        </Group>
        <Alert icon={<IconAlertCircle size={16} />} color="violet">
          All imported plans were discarded. Re-import to start over.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" px="md">
      <Group justify="space-between" mb="md">
        <Group>
          <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={onBack}>
            Back
          </Button>
          <Title order={2}>Verify Test Plans</Title>
          <Badge color="violet" variant="light">
            {plans.length} from {sourceLabel}
          </Badge>
        </Group>
        <Group gap="xs">
          <Checkbox
            label={allApproved ? 'Unapprove all' : 'Approve all'}
            checked={allApproved}
            onChange={(e) => setAllApproved(e.currentTarget.checked)}
          />
        </Group>
      </Group>

      <Text c="dimmed" mb="md" size="sm">
        Review each plan, edit steps as needed, add review notes, then approve. Only approved
        plans get downloaded / sent to a run.
      </Text>

      {bannerError && (
        <Alert color="yellow" icon={<IconAlertCircle size={16} />} mb="md" onClose={() => setBannerError(null)} withCloseButton>
          {bannerError}
        </Alert>
      )}

      {saveStatus && (
        <Alert
          color={saveStatus.errors.length > 0 ? 'yellow' : 'green'}
          icon={<IconCircleCheckFilled size={16} />}
          mb="md"
          onClose={() => setSaveStatus(null)}
          withCloseButton
        >
          {saveStatus.saved} plan{saveStatus.saved === 1 ? '' : 's'} saved to library{saveStatus.subfolder ? ` (folder: ${saveStatus.subfolder})` : ''}.
          {saveStatus.errors.length > 0 && (
            <Stack gap={2} mt="xs">
              <Text size="xs" fw={600}>{saveStatus.errors.length} failed:</Text>
              {saveStatus.errors.map((e, i) => (
                <Text key={i} size="xs">
                  {e.plan?.metadata?.id || `Plan ${i + 1}`}: {e.message}
                </Text>
              ))}
            </Stack>
          )}
        </Alert>
      )}

      <Stack gap="lg" mb="xl">
        {plans.map((plan, idx) => {
          const isApproved = approved.has(idx);
          const yamlVisible = yamlOpen.has(idx);
          const yaml = YAML.stringify(plan);
          return (
            <Card
              key={plan.metadata?.id || idx}
              withBorder
              shadow="sm"
              radius="md"
              p="lg"
              style={{
                borderColor: isApproved ? 'var(--mantine-color-green-4)' : undefined,
                borderWidth: isApproved ? 2 : undefined
              }}
            >
              <Group justify="space-between" mb="sm" wrap="nowrap">
                <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                  {isApproved && (
                    <IconCircleCheckFilled
                      size={22}
                      style={{ color: 'var(--mantine-color-green-6)' }}
                    />
                  )}
                  <TextInput
                    aria-label="Plan name"
                    placeholder="Plan name"
                    value={plan.metadata?.name || ''}
                    onChange={(e) => updateMetadata(idx, { name: e.currentTarget.value })}
                    style={{ flex: 1, minWidth: 200 }}
                    size="md"
                    styles={{ input: { fontWeight: 500, fontSize: 16 } }}
                  />
                </Group>
                <Group gap="xs" wrap="nowrap">
                  <Checkbox
                    label="Approved"
                    checked={isApproved}
                    onChange={() => toggleApproved(idx)}
                  />
                  <Tooltip label="Discard plan">
                    <ActionIcon variant="subtle" color="red" onClick={() => discardPlan(idx)}>
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Group>

              {plan.metadata?.id && (
                <Group gap="xs" mb="sm" wrap="wrap">
                  <Badge variant="light" color="gray">{plan.metadata.id}</Badge>
                  {(plan.metadata.tags || []).map((t) => (
                    <Badge key={t} variant="dot" color="violet">{t}</Badge>
                  ))}
                  <PreconditionsBadge preconditions={plan.metadata?.preconditions} />
                </Group>
              )}

              <Group grow mb="sm">
                <Select
                  label="CCaaS"
                  data={CCAAS_OPTIONS}
                  value={plan.target?.ccaas || 'wxcc'}
                  onChange={(v) => v && updateTarget(idx, { ccaas: v })}
                />
                <TextInput
                  label="Destination"
                  description="E.164, e.g. +15551234567"
                  value={plan.target?.destination || ''}
                  onChange={(e) => updateTarget(idx, { destination: e.currentTarget.value })}
                />
                <Select
                  label="From DID"
                  placeholder={didsLoading ? 'Loading…' : (dids.length === 0 ? 'No DIDs configured' : 'Pick a Chime DID')}
                  data={dids.map((d) => ({ value: d.did, label: d.primary ? `${d.did} (primary)` : d.did }))}
                  value={plan.target?.fromDid || ''}
                  onChange={(v) => updateTarget(idx, { fromDid: v || '' })}
                  searchable
                  disabled={didsLoading || dids.length === 0}
                />
              </Group>

              <Textarea
                label={<Group gap={6}><IconNotes size={14} /><span>Review notes</span></Group>}
                description="Visible in the run report — why this plan exists / what to watch for."
                value={plan.metadata?.notes || ''}
                onChange={(e) => updateMetadata(idx, { notes: e.currentTarget.value })}
                autosize
                minRows={2}
                mb="md"
              />

              <PreconditionsEditor
                value={plan.metadata?.preconditions || []}
                onChange={(next) =>
                  updateMetadata(idx, { preconditions: next.length > 0 ? next : undefined })
                }
              />

              <Divider label="Steps" labelPosition="left" mb="sm" />

              <DragDropContext
                onDragEnd={(result) => {
                  if (!result.destination) return;
                  const from = result.source.index;
                  const to = result.destination.index;
                  if (from === to) return;
                  updateSteps(idx, (arr) => {
                    const copy = [...arr];
                    const [moved] = copy.splice(from, 1);
                    copy.splice(to, 0, moved);
                    return copy;
                  });
                }}
              >
                <Droppable droppableId={`steps-${idx}`}>
                  {(droppableProvided) => (
                    <Stack
                      gap="xs"
                      ref={droppableProvided.innerRef}
                      {...droppableProvided.droppableProps}
                    >
                      {(plan.steps || []).map((step, sidx) => (
                        <Draggable
                          key={step.id || `step-${sidx}`}
                          draggableId={`${idx}-${step.id || sidx}`}
                          index={sidx}
                        >
                          {(dragProvided, snapshot) => (
                            <div
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              style={{ ...dragProvided.draggableProps.style, marginBottom: 4 }}
                            >
                              <PlanStepCard
                                step={step}
                                index={sidx}
                                total={plan.steps.length}
                                dragHandleProps={dragProvided.dragHandleProps}
                                isDragging={snapshot.isDragging}
                                onChange={(next) =>
                                  updateSteps(idx, (arr) =>
                                    arr.map((s, i) => (i === sidx ? next : s))
                                  )
                                }
                                onDelete={() =>
                                  updateSteps(idx, (arr) =>
                                    arr.filter((_, i) => i !== sidx)
                                  )
                                }
                                onMoveUp={() =>
                                  updateSteps(idx, (arr) => {
                                    if (sidx === 0) return arr;
                                    const copy = [...arr];
                                    [copy[sidx - 1], copy[sidx]] = [
                                      copy[sidx],
                                      copy[sidx - 1]
                                    ];
                                    return copy;
                                  })
                                }
                                onMoveDown={() =>
                                  updateSteps(idx, (arr) => {
                                    if (sidx === arr.length - 1) return arr;
                                    const copy = [...arr];
                                    [copy[sidx], copy[sidx + 1]] = [
                                      copy[sidx + 1],
                                      copy[sidx]
                                    ];
                                    return copy;
                                  })
                                }
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {droppableProvided.placeholder}
                    </Stack>
                  )}
                </Droppable>
              </DragDropContext>

              <Group justify="space-between" mt="sm">
                <Menu position="bottom-start" withinPortal>
                  <Menu.Target>
                    <Button
                      variant="light"
                      color="violet"
                      leftSection={<IconPlus size={14} />}
                      rightSection={<IconChevronDown size={14} />}
                    >
                      Add step
                    </Button>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {Object.entries(STEP_TYPE_LABELS).map(([t, label]) => (
                      <Menu.Item
                        key={t}
                        onClick={() =>
                          updateSteps(idx, (arr) => [...arr, makeBlankStep(t, makeStepId(plan, t))])
                        }
                      >
                        {label}
                      </Menu.Item>
                    ))}
                  </Menu.Dropdown>
                </Menu>
                <Button
                  variant="subtle"
                  size="xs"
                  leftSection={<IconCode size={14} />}
                  onClick={() => toggleYaml(idx)}
                >
                  {yamlVisible ? 'Hide' : 'Show'} YAML
                </Button>
              </Group>

              <Collapse in={yamlVisible}>
                <Box mt="sm">
                  <Code block style={{ maxHeight: 240, overflow: 'auto', fontSize: 11 }}>
                    {yaml}
                  </Code>
                </Box>
              </Collapse>
            </Card>
          );
        })}
      </Stack>

      <Card withBorder shadow="md" radius="md" p="md" style={{ position: 'sticky', bottom: 16 }}>
        <Group justify="space-between">
          <Group gap="xs">
            <Badge size="lg" color={approvedPlans.length > 0 ? 'green' : 'gray'} variant="light">
              {approvedPlans.length} approved
            </Badge>
            <Text size="sm" c="dimmed">of {plans.length} plans</Text>
          </Group>
          <Group gap="xs">
            <Button
              variant="default"
              leftSection={<IconDownload size={16} />}
              onClick={handleDownloadApproved}
              disabled={approvedPlans.length === 0}
            >
              Download approved YAML
            </Button>
            <Button
              variant="light"
              color="violet"
              leftSection={<IconCheck size={16} />}
              onClick={openFolderPicker}
              disabled={approvedPlans.length === 0 || !backendConfigured}
              loading={saving}
            >
              Save approved to library
            </Button>
            <Button
              color="violet"
              leftSection={<IconPlayerPlayFilled size={16} />}
              onClick={handleSendApprovedToRun}
              disabled={approvedPlans.length === 0 || saving || !backendConfigured}
              loading={saving}
            >
              {approvedPlans.length > 1 ? `Send ${approvedPlans.length} to Run (batch)` : 'Send approved to Run'}
            </Button>
          </Group>
        </Group>
      </Card>

      <FolderPickerModal
        opened={folderPickerOpen}
        onClose={() => setFolderPickerOpen(false)}
        onConfirm={handleSaveToLibrary}
        mySubfolders={myFolders}
        planCount={approvedPlans.length}
      />

    </Container>
  );
}

/**
 * Save-to-Library folder picker. Required `subfolder` for every save —
 * autocomplete from the current engineer's existing subfolders so the
 * taxonomy compounds, free-text accepted for new folder names. Pattern
 * mirrors the backend SUBFOLDER_PATTERN — frontend rejects bad names with
 * an inline hint before round-tripping to the Lambda's 400.
 */
function FolderPickerModal({ opened, onClose, onConfirm, mySubfolders, planCount }) {
  const [value, setValue] = useState('');
  useEffect(() => {
    if (opened) setValue('');
  }, [opened]);

  const trimmed = value.trim();
  const valid = SUBFOLDER_PATTERN.test(trimmed);
  const showError = trimmed.length > 0 && !valid;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600}>Pick a folder for {planCount === 1 ? 'this plan' : `${planCount} plans`}</Text>}
      centered
      size="md"
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Plans are grouped by folder under your own library namespace. Pick
          an existing folder or type a new one.
        </Text>
        <Autocomplete
          label="Subfolder"
          placeholder='e.g. "Holidays" or "After hours"'
          data={mySubfolders}
          value={value}
          onChange={setValue}
          error={showError ? '1–80 chars; letters, digits, spaces, dashes, underscores. Must start with a letter or digit.' : null}
          data-autofocus
          maxLength={80}
        />
        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={onClose}>Cancel</Button>
          <Button
            color="violet"
            leftSection={<IconCheck size={14} />}
            disabled={!valid}
            onClick={() => onConfirm(trimmed)}
          >
            Save {planCount} {planCount === 1 ? 'plan' : 'plans'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * Inline editor for `metadata.preconditions`. Lives on the Verify Plans
 * screen so engineers can add the manual IVR setup steps the importer
 * couldn't infer (or remove ones it inferred wrong) before saving / running.
 * Empty rows are stripped at onChange time so the schema stays clean.
 */
function PreconditionsEditor({ value, onChange }) {
  const rows = Array.isArray(value) ? value : [];

  const updateRow = (i, patch) => {
    const next = rows.map((r, j) => (j === i ? { ...r, ...patch } : r));
    onChange(next.filter((r) => (r.label || '').trim() || (r.value || '').trim()));
  };
  const removeRow = (i) => onChange(rows.filter((_, j) => j !== i));
  const addRow = () => onChange([...rows, { label: '', value: '' }]);

  return (
    <Stack gap="xs" mb="md">
      <Group gap={6} align="center">
        <IconAlertTriangle size={14} style={{ color: 'var(--mantine-color-orange-7)' }} />
        <Text size="sm" fw={600}>Preconditions (IVR setup before the run)</Text>
      </Group>
      <Text size="xs" c="dimmed">
        Each entry shows up in a pre-run modal so the engineer (and the next person who runs the
        batch) knows what to enable. Common: Holiday flag = ON · After-hours mode = ON · MOTD = enabled.
      </Text>
      {rows.length === 0 && (
        <Text size="xs" c="dimmed" fs="italic">No preconditions — this plan runs without manual IVR setup.</Text>
      )}
      {rows.map((row, i) => (
        <Group key={i} gap="xs" wrap="nowrap" align="flex-start">
          <TextInput
            placeholder="Switch (e.g. Holiday flag)"
            value={row.label || ''}
            onChange={(e) => updateRow(i, { label: e.currentTarget.value })}
            style={{ flex: 1 }}
          />
          <TextInput
            placeholder="Value (e.g. ON)"
            value={row.value || ''}
            onChange={(e) => updateRow(i, { value: e.currentTarget.value })}
            style={{ width: 140 }}
          />
          <TextInput
            placeholder="Where to flip it (optional)"
            value={row.action || ''}
            onChange={(e) => updateRow(i, { action: e.currentTarget.value })}
            style={{ flex: 2 }}
          />
          <ActionIcon variant="subtle" color="red" onClick={() => removeRow(i)} aria-label="remove precondition">
            <IconTrash size={16} />
          </ActionIcon>
        </Group>
      ))}
      <Group>
        <Button size="xs" variant="light" color="violet" onClick={addRow}>+ Add precondition</Button>
      </Group>
    </Stack>
  );
}

export default VerifyPlansScreen;
